/**
 * System Field Registry for Forms Binding Model V2.
 *
 * This is the authoritative catalog for predefined system fields that can be
 * bound to form fields via the V2 binding model. It defines stable bindingKey
 * identities, default widget types, domain groups, and display metadata.
 *
 * Forms own presentation (order, layout, requiredness). The registry owns
 * field meaning: what data a bindingKey refers to, how it should render by
 * default, and which domain group it belongs to.
 *
 * Key conventions:
 *   - bindingKey uses `source.field_name` format (e.g. `patient.full_name`)
 *   - source is the first segment, field path is everything after the first dot
 *   - keys are stable identifiers — never rename without a migration
 */

import type {
  FormFieldType,
  FormFieldBindingSource,
  FormFieldBindingMode,
  DomainGroup,
} from './forms';

// ─── Registry Entry ───

export interface SystemFieldEntry {
  /** Stable binding key in `source.path` format, e.g. `patient.full_name` */
  bindingKey: string;
  /** Binding source */
  source: FormFieldBindingSource;
  /** Human-readable label for builder/admin UI */
  label: string;
  /** Brief description for tooltips or builder help text */
  description?: string;
  /** Default widget type when this field is added to a form */
  defaultWidgetType: FormFieldType;
  /** Default layout hint */
  defaultLayout?: 'half' | 'full';
  /** Domain group for grouped save/load orchestration */
  domainGroup: DomainGroup;
  /** Binding modes allowed for this field. If omitted, all modes are allowed. */
  allowedModes?: FormFieldBindingMode[];
  /** Sample value for builder preview */
  sampleValue: string;
}

// ─── Registry Entries ───

const PATIENT_FIELDS: SystemFieldEntry[] = [
  {
    bindingKey: 'patient.full_name',
    source: 'patient',
    label: 'Patient Name',
    description: 'Full name of the patient',
    defaultWidgetType: 'text',
    defaultLayout: 'full',
    domainGroup: 'demographics',
    sampleValue: 'John Doe',
  },
  {
    bindingKey: 'patient.birth_date',
    source: 'patient',
    label: 'Birth Date',
    description: 'Date of birth',
    defaultWidgetType: 'date',
    defaultLayout: 'half',
    domainGroup: 'demographics',
    sampleValue: '1990-01-15',
  },
  {
    bindingKey: 'patient.sex',
    source: 'patient',
    label: 'Gender',
    description: 'Biological sex / gender',
    defaultWidgetType: 'select',
    defaultLayout: 'half',
    domainGroup: 'demographics',
    sampleValue: 'Male',
  },
  {
    bindingKey: 'patient.patient_number',
    source: 'patient',
    label: 'Patient Number',
    description: 'Unique patient identifier',
    defaultWidgetType: 'text',
    defaultLayout: 'half',
    domainGroup: 'identifiers',
    sampleValue: 'P-00123',
  },
  {
    bindingKey: 'patient.blood_group_abo',
    source: 'patient',
    label: 'Blood Group',
    description: 'ABO blood group classification',
    defaultWidgetType: 'select',
    defaultLayout: 'half',
    domainGroup: 'demographics',
    sampleValue: 'O',
  },
  {
    bindingKey: 'patient.drug_allergies_notes',
    source: 'patient',
    label: 'Drug Allergies',
    description: 'Known drug allergies (free text)',
    defaultWidgetType: 'textarea',
    defaultLayout: 'full',
    domainGroup: 'allergies',
    sampleValue: 'Penicillin',
  },
  {
    bindingKey: 'patient.medical_notes',
    source: 'patient',
    label: 'Medical Notes',
    description: 'General medical notes and conditions',
    defaultWidgetType: 'textarea',
    defaultLayout: 'full',
    domainGroup: 'notes_alerts',
    sampleValue: 'Hypertension, managed with medication',
  },
  {
    bindingKey: 'patient.email',
    source: 'patient',
    label: 'Email',
    description: 'Patient email address',
    defaultWidgetType: 'text',
    defaultLayout: 'half',
    domainGroup: 'contact',
    sampleValue: 'john.doe@email.com',
  },
  {
    bindingKey: 'patient.mobile_number',
    source: 'patient',
    label: 'Mobile Number',
    description: 'Patient mobile phone number',
    defaultWidgetType: 'text',
    defaultLayout: 'half',
    domainGroup: 'contact',
    sampleValue: '+1 555-0123',
  },
  // ── Demographics (expanded) ──
  {
    bindingKey: 'patient.title',
    source: 'patient',
    label: 'Title',
    description: 'Honorific or title (e.g. Mr, Mrs, Dr)',
    defaultWidgetType: 'select',
    defaultLayout: 'half',
    domainGroup: 'demographics',
    sampleValue: 'Mr',
  },
  {
    bindingKey: 'patient.local_name',
    source: 'patient',
    label: 'Local Name',
    description: 'Name in local language/script',
    defaultWidgetType: 'text',
    defaultLayout: 'full',
    domainGroup: 'demographics',
    sampleValue: '',
  },
  {
    bindingKey: 'patient.marital_status',
    source: 'patient',
    label: 'Marital Status',
    description: 'Marital status',
    defaultWidgetType: 'select',
    defaultLayout: 'half',
    domainGroup: 'demographics',
    sampleValue: 'Single',
  },
  {
    bindingKey: 'patient.nationality',
    source: 'patient',
    label: 'Nationality',
    description: 'Country of nationality',
    defaultWidgetType: 'text',
    defaultLayout: 'half',
    domainGroup: 'demographics',
    sampleValue: 'Thai',
  },
  {
    bindingKey: 'patient.country_of_birth',
    source: 'patient',
    label: 'Country of Birth',
    description: 'Country where the patient was born',
    defaultWidgetType: 'text',
    defaultLayout: 'half',
    domainGroup: 'demographics',
    sampleValue: 'Thailand',
  },
  {
    bindingKey: 'patient.race',
    source: 'patient',
    label: 'Race / Ethnicity',
    description: 'Race or ethnicity',
    defaultWidgetType: 'text',
    defaultLayout: 'half',
    domainGroup: 'demographics',
    sampleValue: '',
  },
  {
    bindingKey: 'patient.religion',
    source: 'patient',
    label: 'Religion',
    description: 'Religious affiliation',
    defaultWidgetType: 'text',
    defaultLayout: 'half',
    domainGroup: 'demographics',
    sampleValue: '',
  },
  {
    bindingKey: 'patient.occupation',
    source: 'patient',
    label: 'Occupation',
    description: 'Patient occupation or profession',
    defaultWidgetType: 'text',
    defaultLayout: 'half',
    domainGroup: 'demographics',
    sampleValue: 'Engineer',
  },
  {
    bindingKey: 'patient.literacy_level',
    source: 'patient',
    label: 'Literacy Level',
    description: 'Patient literacy level',
    defaultWidgetType: 'select',
    defaultLayout: 'half',
    domainGroup: 'demographics',
    sampleValue: '',
  },
  // ── Clinical / Administrative ──
  {
    bindingKey: 'patient.assigned_doctor',
    source: 'patient',
    label: 'Assigned Doctor',
    description: 'Primary assigned doctor or provider',
    defaultWidgetType: 'text',
    defaultLayout: 'half',
    domainGroup: 'demographics',
    sampleValue: 'Dr. Jane Smith',
  },
  {
    bindingKey: 'patient.referred_by',
    source: 'patient',
    label: 'Referred By',
    description: 'Source of referral',
    defaultWidgetType: 'text',
    defaultLayout: 'half',
    domainGroup: 'demographics',
    sampleValue: '',
  },
  {
    bindingKey: 'patient.location',
    source: 'patient',
    label: 'Location',
    description: 'Patient location or branch',
    defaultWidgetType: 'text',
    defaultLayout: 'half',
    domainGroup: 'demographics',
    sampleValue: '',
  },
  {
    bindingKey: 'patient.department_name',
    source: 'patient',
    label: 'Department',
    description: 'Department name (for occupational clinics)',
    defaultWidgetType: 'text',
    defaultLayout: 'half',
    domainGroup: 'demographics',
    sampleValue: '',
  },
  {
    bindingKey: 'patient.job_position',
    source: 'patient',
    label: 'Job Position',
    description: 'Job title or position (for occupational clinics)',
    defaultWidgetType: 'text',
    defaultLayout: 'half',
    domainGroup: 'demographics',
    sampleValue: '',
  },
  {
    bindingKey: 'patient.shift',
    source: 'patient',
    label: 'Shift',
    description: 'Work shift (for occupational clinics)',
    defaultWidgetType: 'text',
    defaultLayout: 'half',
    domainGroup: 'demographics',
    sampleValue: '',
  },
  // ── Medication / Allergies (expanded) ──
  {
    bindingKey: 'patient.blood_group_rh',
    source: 'patient',
    label: 'Rh Factor',
    description: 'Rh blood group antigen (positive/negative)',
    defaultWidgetType: 'select',
    defaultLayout: 'half',
    domainGroup: 'demographics',
    sampleValue: '+',
  },
  {
    bindingKey: 'patient.food_allergies_notes',
    source: 'patient',
    label: 'Food Allergies',
    description: 'Known food allergies (free text)',
    defaultWidgetType: 'textarea',
    defaultLayout: 'full',
    domainGroup: 'allergies',
    sampleValue: 'Shellfish',
  },
  {
    bindingKey: 'patient.medical_alerts',
    source: 'patient',
    label: 'Medical Alerts',
    description: 'Active medical alerts',
    defaultWidgetType: 'textarea',
    defaultLayout: 'full',
    domainGroup: 'notes_alerts',
    sampleValue: '',
  },
  {
    bindingKey: 'patient.past_medication_notes',
    source: 'patient',
    label: 'Past Medication',
    description: 'Past medication history (free text)',
    defaultWidgetType: 'textarea',
    defaultLayout: 'full',
    domainGroup: 'medication',
    sampleValue: '',
  },
  {
    bindingKey: 'patient.current_medication_notes',
    source: 'patient',
    label: 'Current Medication',
    description: 'Current medications (free text)',
    defaultWidgetType: 'textarea',
    defaultLayout: 'full',
    domainGroup: 'medication',
    sampleValue: '',
  },
  {
    bindingKey: 'patient.medical_history',
    source: 'patient',
    label: 'Medical History',
    description: 'Past medical history (free text)',
    defaultWidgetType: 'textarea',
    defaultLayout: 'full',
    domainGroup: 'notes_alerts',
    sampleValue: '',
  },
  // ── Contact (expanded) ──
  {
    bindingKey: 'patient.formal_telephone',
    source: 'patient',
    label: 'Telephone (Formal)',
    description: 'Formal/home telephone number',
    defaultWidgetType: 'text',
    defaultLayout: 'half',
    domainGroup: 'contact',
    sampleValue: '+1 555-0100',
  },
  {
    bindingKey: 'patient.formal_residential_address',
    source: 'patient',
    label: 'Residential Address',
    description: 'Formal residential address',
    defaultWidgetType: 'text',
    defaultLayout: 'full',
    domainGroup: 'contact',
    sampleValue: '123 Main Street',
  },
  {
    bindingKey: 'patient.formal_province_state',
    source: 'patient',
    label: 'Province / State',
    description: 'Province or state of residence',
    defaultWidgetType: 'text',
    defaultLayout: 'half',
    domainGroup: 'contact',
    sampleValue: 'Bangkok',
  },
  {
    bindingKey: 'patient.formal_district',
    source: 'patient',
    label: 'District',
    description: 'District of residence',
    defaultWidgetType: 'text',
    defaultLayout: 'half',
    domainGroup: 'contact',
    sampleValue: '',
  },
  {
    bindingKey: 'patient.formal_postal_code',
    source: 'patient',
    label: 'Postal Code',
    description: 'Postal / ZIP code',
    defaultWidgetType: 'text',
    defaultLayout: 'half',
    domainGroup: 'contact',
    sampleValue: '10110',
  },
  {
    bindingKey: 'patient.mobile_country_code',
    source: 'patient',
    label: 'Mobile Country Code',
    description: 'Country dial code for mobile number',
    defaultWidgetType: 'text',
    defaultLayout: 'half',
    domainGroup: 'contact',
    sampleValue: '+1',
  },
  {
    bindingKey: 'patient.formal_unit',
    source: 'patient',
    label: 'Unit / Apt Number',
    description: 'Unit, apartment, or suite number',
    defaultWidgetType: 'text',
    defaultLayout: 'half',
    domainGroup: 'contact',
    sampleValue: 'Unit 4B',
  },
  {
    bindingKey: 'patient.formal_residential_address_local',
    source: 'patient',
    label: 'Residential Address (Local)',
    description: 'Formal residential address in local language',
    defaultWidgetType: 'text',
    defaultLayout: 'full',
    domainGroup: 'contact',
    sampleValue: '',
  },
  {
    bindingKey: 'patient.formal_sub_district',
    source: 'patient',
    label: 'Sub-District',
    description: 'Sub-district of residence',
    defaultWidgetType: 'text',
    defaultLayout: 'half',
    domainGroup: 'contact',
    sampleValue: '',
  },
  // ── Present Address ──
  {
    bindingKey: 'patient.present_telephone',
    source: 'patient',
    label: 'Telephone (Present)',
    description: 'Present address telephone number',
    defaultWidgetType: 'text',
    defaultLayout: 'half',
    domainGroup: 'contact',
    sampleValue: '',
  },
  {
    bindingKey: 'patient.present_postal_code',
    source: 'patient',
    label: 'Postal Code (Present)',
    description: 'Postal / ZIP code at present address',
    defaultWidgetType: 'text',
    defaultLayout: 'half',
    domainGroup: 'contact',
    sampleValue: '',
  },
  {
    bindingKey: 'patient.present_unit',
    source: 'patient',
    label: 'Unit / Apt (Present)',
    description: 'Unit number at present address',
    defaultWidgetType: 'text',
    defaultLayout: 'half',
    domainGroup: 'contact',
    sampleValue: '',
  },
  {
    bindingKey: 'patient.present_address',
    source: 'patient',
    label: 'Address (Present)',
    description: 'Present address',
    defaultWidgetType: 'text',
    defaultLayout: 'full',
    domainGroup: 'contact',
    sampleValue: '',
  },
  {
    bindingKey: 'patient.present_address_local',
    source: 'patient',
    label: 'Address (Present, Local)',
    description: 'Present address in local language',
    defaultWidgetType: 'text',
    defaultLayout: 'full',
    domainGroup: 'contact',
    sampleValue: '',
  },
  {
    bindingKey: 'patient.present_province_state',
    source: 'patient',
    label: 'Province / State (Present)',
    description: 'Province or state at present address',
    defaultWidgetType: 'text',
    defaultLayout: 'half',
    domainGroup: 'contact',
    sampleValue: '',
  },
  {
    bindingKey: 'patient.present_district',
    source: 'patient',
    label: 'District (Present)',
    description: 'District at present address',
    defaultWidgetType: 'text',
    defaultLayout: 'half',
    domainGroup: 'contact',
    sampleValue: '',
  },
  {
    bindingKey: 'patient.present_sub_district',
    source: 'patient',
    label: 'Sub-District (Present)',
    description: 'Sub-district at present address',
    defaultWidgetType: 'text',
    defaultLayout: 'half',
    domainGroup: 'contact',
    sampleValue: '',
  },
  // ── Office ──
  {
    bindingKey: 'patient.office_telephone',
    source: 'patient',
    label: 'Office Telephone',
    description: 'Office telephone number',
    defaultWidgetType: 'text',
    defaultLayout: 'half',
    domainGroup: 'contact',
    sampleValue: '',
  },
  {
    bindingKey: 'patient.office_address',
    source: 'patient',
    label: 'Office Address',
    description: 'Office address',
    defaultWidgetType: 'text',
    defaultLayout: 'full',
    domainGroup: 'contact',
    sampleValue: '',
  },
  // ── Structured Allergies (multi-row → patient_allergies table) ──
  {
    bindingKey: 'patient.allergies_drug',
    source: 'patient',
    label: 'Drug Allergies (Structured)',
    description:
      'Structured drug allergy substances — each selection creates a patient_allergies row',
    defaultWidgetType: 'multi-select',
    defaultLayout: 'full',
    domainGroup: 'allergies',
    sampleValue: '["Penicillin","Aspirin"]',
  },
  {
    bindingKey: 'patient.allergies_food',
    source: 'patient',
    label: 'Food Allergies (Structured)',
    description:
      'Structured food allergy substances — each selection creates a patient_allergies row',
    defaultWidgetType: 'multi-select',
    defaultLayout: 'full',
    domainGroup: 'allergies',
    sampleValue: '["Shellfish","Peanuts"]',
  },
];

const ENCOUNTER_FIELDS: SystemFieldEntry[] = [
  {
    bindingKey: 'encounter.type',
    source: 'encounter',
    label: 'Encounter Type',
    description: 'Type/category of the encounter',
    defaultWidgetType: 'select',
    defaultLayout: 'half',
    domainGroup: 'encounter',
    allowedModes: ['read_only', 'default_only'],
    sampleValue: 'Consultation',
  },
  {
    bindingKey: 'encounter.started_at',
    source: 'encounter',
    label: 'Encounter Date',
    description: 'When the encounter started',
    defaultWidgetType: 'date',
    defaultLayout: 'half',
    domainGroup: 'encounter',
    allowedModes: ['read_only'],
    sampleValue: '2026-03-20T09:30:00Z',
  },
  {
    bindingKey: 'encounter.provider_name',
    source: 'encounter',
    label: 'Provider Name',
    description: 'Name of the attending provider',
    defaultWidgetType: 'text',
    defaultLayout: 'half',
    domainGroup: 'encounter',
    allowedModes: ['read_only'],
    sampleValue: 'Dr. Jane Smith',
  },
];

/**
 * form_context fields are system-level context values available at form render
 * time. Runtime resolution is not yet implemented but the registry entries
 * are declared here so the builder can present them and schemas can reference them.
 */
const FORM_CONTEXT_FIELDS: SystemFieldEntry[] = [
  {
    bindingKey: 'form_context.current_date',
    source: 'form_context',
    label: 'Current Date',
    description: "Today's date at form render time",
    defaultWidgetType: 'date',
    defaultLayout: 'half',
    domainGroup: 'encounter',
    allowedModes: ['read_only'],
    sampleValue: '2026-04-04',
  },
  {
    bindingKey: 'form_context.clinic_name',
    source: 'form_context',
    label: 'Clinic Name',
    description: 'Name of the current clinic',
    defaultWidgetType: 'text',
    defaultLayout: 'half',
    domainGroup: 'encounter',
    allowedModes: ['read_only'],
    sampleValue: 'Main Street Dental',
  },
];

// ─── Internal index ───

const ALL_ENTRIES: SystemFieldEntry[] = [
  ...PATIENT_FIELDS,
  ...ENCOUNTER_FIELDS,
  ...FORM_CONTEXT_FIELDS,
];

const _byKey = new Map<string, SystemFieldEntry>();
for (const entry of ALL_ENTRIES) {
  _byKey.set(entry.bindingKey, entry);
}

// ─── Public API ───

/** Get all registered system fields. */
export function getAllSystemFields(): readonly SystemFieldEntry[] {
  return ALL_ENTRIES;
}

/** Look up a system field by its stable binding key (e.g. `patient.full_name`). */
export function getSystemFieldByKey(bindingKey: string): SystemFieldEntry | undefined {
  return _byKey.get(bindingKey);
}

/** List system fields filtered by source. */
export function getSystemFieldsBySource(
  source: FormFieldBindingSource,
): readonly SystemFieldEntry[] {
  return ALL_ENTRIES.filter((e) => e.source === source);
}

/** List system fields filtered by domain group. */
export function getSystemFieldsByDomainGroup(group: DomainGroup): readonly SystemFieldEntry[] {
  return ALL_ENTRIES.filter((e) => e.domainGroup === group);
}

/**
 * Check whether a given binding mode is allowed for a system field.
 * Returns true if the field has no restrictions or the mode is in allowedModes.
 * Returns true for unknown bindingKeys (non-registry fields have no restrictions).
 */
export function isBindingModeAllowed(bindingKey: string, mode: FormFieldBindingMode): boolean {
  const entry = _byKey.get(bindingKey);
  if (!entry) return true; // Unknown key — no restriction
  if (!entry.allowedModes) return true; // No restriction defined
  return entry.allowedModes.includes(mode);
}

/**
 * Derive the flat binding source list format used by the legacy builder UI.
 * This is a compatibility adapter — the registry is the source of truth.
 */
export function getBindingSourceOptions(): { value: string; label: string }[] {
  return ALL_ENTRIES.map((e) => ({ value: e.bindingKey, label: e.label }));
}

/**
 * Derive the sample data map used by preview/template overlay.
 * Keys are `source.path` strings, values are sample display strings.
 */
export function getSampleDataMap(): Record<string, string> {
  const map: Record<string, string> = {};
  for (const e of ALL_ENTRIES) {
    map[e.bindingKey] = e.sampleValue;
  }
  return map;
}
